import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import styled from "styled-components";
import theme from "../../../style/theme";
import {DeliveryGroupType} from "../../../types/schedule/delivery-group-type";
import ArrowDownLineIcon from "../../icons/ArrowDownLineIcon/ArrowDownLineIcon";
import ArrowUpLineIcon from "../../icons/ArrowUpLineIcon/ArrowUpLineIcon";
import ChevronLeftLineIcon from "../../icons/ChevronLeftLineIcon/ChevronLeftLineIcon";

export interface MarkerProps {
    children?: string;
    timeRange?: string;
    aliasLabel?: string;
    totalDeliveries?: number;
    checkedDeliveries?: number;
    focus?: boolean;
    type?: DeliveryGroupType;
    deliveryGroupOrder?: number;
    style?: React.CSSProperties;
}

const ScheduleCreatePin: React.FC<MarkerProps> = (
    {
        children,
        timeRange,
        aliasLabel,
        totalDeliveries,
        checkedDeliveries,
        focus,
        type,
        deliveryGroupOrder,
        style,
    }
) => {
    const completed = totalDeliveries === checkedDeliveries;

    return (
        <div style={style}>
            <MarkerBox>
                <MarkerContainer>
                    <MarkerPointer
                        className={`${focus ? "focus " : ""}  ${
                            type === DeliveryGroupType.PickUp ? "pick-up " : ""
                        }  ${type === DeliveryGroupType.DropOff ? "drop-off " : ""}`}
                    >
            <span
                style={{
                    marginLeft: "4px",
                    marginRight: "4px",
                }}
            >
              {focus ? (
                  <span
                      style={{
                          fontWeight: 600,
                          fontSize: "16px",
                          lineHeight: "16px",
                          color: "#FFFFFF",
                      }}
                  >
                  {deliveryGroupOrder}번
                </span>
              ) : (
                  <>
                      {type === DeliveryGroupType.PickUp && (
                          <ArrowUpLineIcon
                              fill={`${theme.palette.primitives.blue400}`}
                              width={"12px"}
                              height={"12px"}
                          />
                      )}
                      {type === DeliveryGroupType.DropOff && (
                          <ArrowDownLineIcon
                              fill={`${theme.palette.primitives.yellow400}`}
                              width={"12px"}
                              height={"12px"}
                          />
                      )}
                  </>
              )}
            </span>

                        <AliasLabel className={`${focus ? "focus " : ""}`}>
                            {aliasLabel}
                        </AliasLabel>
                        <NumberBox
                            className={`${completed && "completed "} ${
                                focus ? "focus " : ""
                            } ${checkedDeliveries ? "checkedDeliveries " : ""}`}
                        >
                            {completed ? (
                                <ChevronLeftLineIcon
                                    width="7px"
                                    height="11px"
                                    style={{transform: "rotate(-90deg)"}}
                                />
                            ) : (
                                <>
                                    {checkedDeliveries ? checkedDeliveries + "/" : ""}
                                    {totalDeliveries}
                                </>
                            )}
                        </NumberBox>
                    </MarkerPointer>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <PinBorder
                            className={`${
                                type === DeliveryGroupType.PickUp ? "pick-up " : ""
                            }  ${type === DeliveryGroupType.DropOff ? "drop-off " : ""}`}
                        />
                    </div>
                </MarkerContainer>
            </MarkerBox>
        </div>
    );
};

export default ScheduleCreatePin;

const MarkerBox = styled.div`
  position: relative;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -ms-flex-direction: column;
  flex-direction: column;
`;

const MarkerContainer = styled.div`
  position: absolute;
  transform: translate(calc(-50% + 0px), calc(-50% - 23px));
`;

const MarkerPointer = styled.div`
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;

  border-radius: 999px;
  background: ${theme.palette.primitives.white};
  padding: 4px 6px;
  cursor: pointer;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.pick-up {
    border: 1px solid #009ff8;
  }

  &.drop-off {
    border: 1px solid ${theme.palette.primitives.yellow400};
  }

  &.focus.pick-up {
    background-color: ${theme.palette.primitives.blue400};
  }

  &.focus.drop-off {
    background-color: ${theme.palette.primitives.yellow400};
  }

  &:not(.focus):hover {
    background-color: ${theme.palette.primitives.gray100};
  }

  &.focus.pick-up:hover {
    border: 1px solid ${theme.palette.primitives.blue200};
    background-color: ${theme.palette.primitives.blue300};
  }

  &.focus.drop-off:hover {
    border: 1px solid ${theme.palette.primitives.yellow200};
    background-color: ${theme.palette.primitives.yellow300};
  }
`;

const NumberBox = styled.span`
  position: absolute;
  top: -7px;
  right: -3px;
  background-color: ${theme.palette.primitives.gray800};
  font-size: 12px;
  border-radius: 3px;
  padding: 2px;
  color: ${theme.palette.primitives.white};
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.checkedDeliveries {
    top: -10px;
    right: -16px;
  }

  &.focus.checkedDeliveries {
    background-color: ${theme.palette.primitives.green500};
  }

  &.completed {
    top: -9px;
    right: -6px;
  }
`;

const AliasLabel = styled.span`
  max-width: 160px;
  font-weight: 600;
  font-size: 16px;
  padding: 0 4px;
  line-height: 16px;
  margin-right: 2px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.focus {
    color: ${theme.palette.primitives.white};
  }
`;

const PinBorder = styled.div`
  border-left: 3px solid #009ff8;
  height: 16px;

  &.pick-up {
    border-left: 3px solid #009ff8;
  }

  &.drop-off {
    border-left: 3px solid ${theme.palette.primitives.yellow400};
  }
`;

ScheduleCreatePin.defaultProps = {
    timeRange: "11:20",
    totalDeliveries: 6,
    checkedDeliveries: 3,
    focus: false,
    type: DeliveryGroupType.PickUp,
    deliveryGroupOrder: 0,
};
